import FadeIn from './FadeIn';

const gold = '#C8A96E';
const charcoal = '#1A1916';
const secondary = '#6B6862';
const tertiary = '#9A9690';
const divider = '#E8E6E2';

const SectionRule = () => (
  <div style={{ width: '32px', height: '1px', backgroundColor: gold, margin: '0 auto 72px' }} />
);

const GoldButton = ({ href, onClick, children }) => {
  const base = {
    display: 'inline-flex', alignItems: 'center', gap: '10px',
    padding: '13px 28px', fontSize: '0.75rem', fontWeight: 500,
    letterSpacing: '0.18em', textTransform: 'uppercase',
    border: `1px solid ${gold}`, color: gold,
    backgroundColor: 'transparent', cursor: 'pointer',
    transition: 'background-color 0.25s, color 0.25s', textDecoration: 'none',
  };
  if (href) return (
    <a href={href} target="_blank" rel="noopener noreferrer" style={base}
      onMouseEnter={e => Object.assign(e.currentTarget.style, { backgroundColor: gold, color: '#fff' })}
      onMouseLeave={e => Object.assign(e.currentTarget.style, { backgroundColor: 'transparent', color: gold })}
    >{children}</a>
  );
  return (
    <button style={base} onClick={onClick}
      onMouseEnter={e => Object.assign(e.currentTarget.style, { backgroundColor: gold, color: '#fff' })}
      onMouseLeave={e => Object.assign(e.currentTarget.style, { backgroundColor: 'transparent', color: gold })}
    >{children}</button>
  );
};

const Label = ({ children }) => (
  <p style={{ color: gold, fontSize: '0.7rem', fontWeight: 500, letterSpacing: '0.22em', textTransform: 'uppercase', marginBottom: '16px' }}>{children}</p>
);

const WritingsPage = ({ language = 'en' }) => {
  return (
    <div className="min-h-screen bg-white">
      <div style={{ paddingTop: 'env(safe-area-inset-top)', position: 'sticky', top: 0, zIndex: 50, backgroundColor: '#fff' }} />

      {/* ─── HERO ─────────────────────────────────────────────── */}
      <section className="px-6 text-center" style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
        <Label>{language === 'es' ? 'Escritos' : 'Writings'}</Label>
        <h1 className="font-light" style={{ color: charcoal, fontSize: 'clamp(3rem, 8vw, 5.5rem)', lineHeight: '1.1', letterSpacing: '-0.02em', marginBottom: '20px' }}>
          {language === 'es' ? 'Escritos' : 'Writings'}
        </h1>
        <p className="font-light" style={{ color: secondary, lineHeight: '1.7', maxWidth: '480px', margin: '0 auto 28px' }}>
          {language === 'es'
            ? 'Marcos de trabajo, investigación y perspectivas sobre bienes raíces, capital y sistemas de largo horizonte.'
            : 'Frameworks, research, and perspectives on real estate, capital, and long-horizon systems.'}
        </p>
        <div style={{ width: '32px', height: '1px', backgroundColor: gold, margin: '0 auto 24px' }} />
        <p className="font-light text-sm" style={{ color: tertiary, lineHeight: '2', maxWidth: '512px', margin: '0 auto' }}>
          This is a collection of essays, field notes, and institutional-grade research exploring how we design, finance, and scale the built environment — and how these systems evolve over time.
        </p>
        <div className="scroll-arrow" style={{ position: 'absolute', bottom: '40px', left: '50%', transform: 'translateX(-50%)' }}>
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M10 3v14M3 10l7 7 7-7" stroke={gold} strokeWidth="1" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </div>
      </section>

      {/* ─── SECTION 1 — FEATURED THESIS ──────────────────────── */}
      <section className="pt-12 pb-20 px-6">
        <FadeIn>
          <div style={{ maxWidth: '672px', margin: '0 auto' }}>
            <Label>{language === 'es' ? 'Tesis Destacada' : 'Featured Thesis'}</Label>
            <h2 className="font-light" style={{ color: charcoal, fontSize: 'clamp(1.75rem, 4vw, 2.5rem)', lineHeight: '1.2', marginBottom: '32px' }}>
              {language === 'es' ? 'Tesis Destacada' : 'Featured Thesis'}
            </h2>
            <div style={{ border: `1px solid ${divider}`, padding: '32px' }}>
              <p style={{ color: gold, fontSize: '0.7rem', fontWeight: 500, letterSpacing: '0.22em', textTransform: 'uppercase', marginBottom: '12px' }}>Long-form Thesis</p>
              <p className="font-normal" style={{ color: charcoal, fontSize: '1.25rem', lineHeight: '1.3', marginBottom: '16px' }}>Infinite Real Estate</p>
              <p className="font-light text-sm" style={{ color: secondary, lineHeight: '2', marginBottom: '28px' }}>
                A long-form thesis exploring how the built environment is evolving through the convergence of technology, sustainability, and institutional capital.
              </p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
                <button disabled style={{ display: 'inline-flex', alignItems: 'center', gap: '10px', padding: '13px 28px', fontSize: '0.75rem', fontWeight: 500, letterSpacing: '0.18em', textTransform: 'uppercase', border: '1px solid #D0CEC9', color: '#B0AEA9', backgroundColor: 'transparent', cursor: 'not-allowed' }}>{language === 'es' ? 'Leer la Tesis Completa' : 'Read Full Thesis'}</button>
                <button disabled style={{ display: 'inline-flex', alignItems: 'center', gap: '10px', padding: '13px 28px', fontSize: '0.75rem', fontWeight: 500, letterSpacing: '0.18em', textTransform: 'uppercase', border: '1px solid #D0CEC9', color: '#B0AEA9', backgroundColor: 'transparent', cursor: 'not-allowed' }}>{language === 'es' ? 'Descargar PDF' : 'Download PDF'}</button>
              </div>
            </div>
          </div>
        </FadeIn>
      </section>

      <SectionRule />

      {/* ─── SECTION 2 — CORE THEMES ──────────────────────────── */}
      <section className="py-20 px-6">
        <FadeIn>
          <div style={{ maxWidth: '672px', margin: '0 auto' }}>
            <Label>{language === 'es' ? 'Áreas de Enfoque' : 'Areas of Focus'}</Label>
            <h2 className="font-light" style={{ color: charcoal, fontSize: 'clamp(1.75rem, 4vw, 2.5rem)', lineHeight: '1.2', marginBottom: '16px' }}>
              {language === 'es' ? 'Áreas Principales de Enfoque' : 'Core Areas of Focus'}
            </h2>
            <p className="font-light text-sm" style={{ color: tertiary, lineHeight: '1.9', marginBottom: '36px' }}>
              {language === 'es' ? 'En lugar de publicaciones aisladas, el pensamiento se organiza en sistemas:' : 'Rather than isolated posts, thinking is organized into systems:'}
            </p>
            {[
              { title: 'Real Estate Development', desc: 'Execution, entitlements, capital stacks, and delivery systems.' },
              { title: 'Capital & Investment', desc: 'Blended capital, institutional frameworks, and long-term value creation.' },
              { title: 'AI & The Built Environment', desc: 'How artificial intelligence is transforming development, design, and operations.' },
              { title: 'Sustainability & Regeneration', desc: 'Carbon, materials, energy systems, and the future of sustainable cities.' },
              { title: 'Global Markets (US / LATAM)', desc: 'Cross-border development, emerging markets, and regional strategy.' },
              { title: 'Systems & Platform Thinking', desc: 'How integrated platforms like Infinity are designed, governed, and scaled.' },
            ].map((item, i) => (
              <div key={i} style={{ borderBottom: `1px solid ${divider}`, padding: '20px 0', display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
                <div style={{ width: '6px', height: '1px', backgroundColor: gold, marginTop: '9px', flexShrink: 0 }} />
                <div>
                  <p className="font-normal text-sm" style={{ color: charcoal, marginBottom: '5px' }}>{item.title}</p>
                  <p className="font-light" style={{ color: tertiary, fontSize: '0.75rem', lineHeight: '1.9' }}>{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </FadeIn>
      </section>

      <SectionRule />

      {/* ─── SECTION 3 — LATEST WRITINGS ──────────────────────── */}
      <section className="py-20 px-6">
        <FadeIn>
          <div style={{ maxWidth: '672px', margin: '0 auto' }}>
            <Label>{language === 'es' ? 'Más Recientes' : 'Latest'}</Label>
            <h2 className="font-light" style={{ color: charcoal, fontSize: 'clamp(1.75rem, 4vw, 2.5rem)', lineHeight: '1.2', marginBottom: '36px' }}>
              {language === 'es' ? 'Escritos Recientes' : 'Latest Writings'}
            </h2>
            {[
              { title: "Why Modular Construction Is Not About Cost — It's About Time", tags: 'Development · Construction · Systems', desc: 'A reframe of industrialized construction as a time-reduction tool rather than a cost play.', url: 'https://medium.com/@zeus_11459' },
              { title: 'Green IRR: Rethinking Return in Sustainable Development', tags: 'Capital · Sustainability', desc: 'How environmental performance impacts long-term asset value and investor returns.', url: 'https://medium.com/@zeus_11459' },
              { title: 'AI in Development: From Feasibility to Execution', tags: 'AI · Development', desc: 'Mapping how AI integrates across the development lifecycle — from site selection to operations.', url: 'https://medium.com/@zeus_11459' },
            ].map((item, i) => (
              <div key={i} style={{ borderBottom: `1px solid ${divider}`, padding: '24px 0', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '24px' }}>
                <div style={{ flex: 1 }}>
                  <p style={{ color: gold, fontSize: '0.7rem', fontWeight: 500, letterSpacing: '0.18em', textTransform: 'uppercase', marginBottom: '8px' }}>{item.tags}</p>
                  <p className="font-normal text-sm" style={{ color: charcoal, marginBottom: '8px', lineHeight: '1.5' }}>{item.title}</p>
                  <p className="font-light" style={{ color: tertiary, fontSize: '0.75rem', lineHeight: '1.9' }}>{item.desc}</p>
                </div>
                <a href={item.url} target="_blank" rel="noopener noreferrer" style={{ color: gold, fontSize: '0.75rem', letterSpacing: '0.06em', textDecoration: 'none', flexShrink: 0, marginTop: '2px' }}
                  onMouseEnter={e => (e.currentTarget.style.opacity = '0.7')}
                  onMouseLeave={e => (e.currentTarget.style.opacity = '1')}
                >Read →</a>
              </div>
            ))}
          </div>
        </FadeIn>
      </section>

      <SectionRule />

      {/* ─── SECTION 4 — RESEARCH ─────────────────────────────── */}
      <section className="py-20 px-6">
        <FadeIn>
          <div style={{ maxWidth: '672px', margin: '0 auto' }}>
            <Label>{language === 'es' ? 'Investigación' : 'Research'}</Label>
            <h2 className="font-light" style={{ color: charcoal, fontSize: 'clamp(1.75rem, 4vw, 2.5rem)', lineHeight: '1.2', marginBottom: '36px' }}>
              {language === 'es' ? 'Investigación y Documentos Técnicos' : 'Research & White Papers'}
            </h2>
            {[
              { title: 'Green IRR: Rethinking Return in Sustainable Development', desc: 'How environmental performance impacts long-term asset value.' },
              { title: 'Blended Capital Stacks in Real Estate Development', desc: 'Structuring capital across institutional, impact, and concessional layers.' },
              { title: 'AI in Development: From Feasibility to Execution', desc: 'Mapping how AI integrates across the development lifecycle.' },
            ].map((item, i) => (
              <div key={i} style={{ borderBottom: `1px solid ${divider}`, padding: '20px 0', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '24px' }}>
                <div style={{ flex: 1 }}>
                  <p className="font-normal text-sm" style={{ color: charcoal, marginBottom: '6px' }}>{item.title}</p>
                  <p className="font-light" style={{ color: tertiary, fontSize: '0.75rem', lineHeight: '1.9' }}>{item.desc}</p>
                </div>
                <button disabled style={{ color: '#B0AEA9', fontSize: '0.75rem', letterSpacing: '0.06em', flexShrink: 0, marginTop: '2px', background: 'none', border: 'none', cursor: 'not-allowed', padding: 0 }}>{language === 'es' ? 'Descargar PDF' : 'Download PDF'}</button>
              </div>
            ))}
          </div>
        </FadeIn>
      </section>

      <SectionRule />

      {/* ─── SECTION 5 — FIELD NOTES ──────────────────────────── */}
      <section className="py-20 px-6">
        <FadeIn>
          <div style={{ maxWidth: '672px', margin: '0 auto' }}>
            <Label>{language === 'es' ? 'Notas de Campo' : 'Field Notes'}</Label>
            <h2 className="font-light" style={{ color: charcoal, fontSize: 'clamp(1.75rem, 4vw, 2.5rem)', lineHeight: '1.2', marginBottom: '16px' }}>
              {language === 'es' ? 'Notas de Campo' : 'Field Notes'}
            </h2>
            <p className="font-light text-sm" style={{ color: tertiary, lineHeight: '2', marginBottom: '32px' }}>
              {language === 'es' ? 'Ideas breves de reuniones, visitas a sitios y eventos.' : 'Short-form insights from meetings, site visits, and events.'}
            </p>
            {['Lessons from IMN ESG Conference', 'What Developers Are Getting Wrong About AI', 'Notes on LATAM Housing Markets'].map((note, i) => (
              <div key={i} style={{ borderBottom: `1px solid ${divider}`, padding: '16px 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <p className="font-normal text-sm" style={{ color: charcoal }}>{note}</p>
                <button disabled style={{ color: '#B0AEA9', fontSize: '0.75rem', letterSpacing: '0.06em', flexShrink: 0, background: 'none', border: 'none', cursor: 'not-allowed', padding: 0 }}>Read →</button>
              </div>
            ))}
          </div>
        </FadeIn>
      </section>

      <SectionRule />

      {/* ─── SECTION 6 — EXTERNAL ─────────────────────────────── */}
      <section className="py-16 px-6 text-center">
        <FadeIn>
          <div style={{ maxWidth: '512px', margin: '0 auto' }}>
            <Label>{language === 'es' ? 'Distribución' : 'Distribution'}</Label>
            <h2 className="font-light" style={{ color: charcoal, fontSize: 'clamp(1.75rem, 4vw, 2.5rem)', lineHeight: '1.2', marginBottom: '20px' }}>
              {language === 'es' ? 'Más Allá de Esta Plataforma' : 'Beyond This Platform'}
            </h2>
            <p className="font-light text-sm" style={{ color: secondary, lineHeight: '2', marginBottom: '32px' }}>
              {language === 'es' ? 'Algunos escritos también se publican externamente para mayor alcance.' : 'Some writings are also published externally for broader reach.'}
            </p>
            <GoldButton href="https://medium.com/@zeus_11459">
              {language === 'es' ? 'Ver en Medium' : 'View on Medium'}
            </GoldButton>
          </div>
        </FadeIn>
      </section>
    </div>
  );
};

export default WritingsPage;
